import { DefaultLoadingManager } from 'three'

// Static id of the progress-bar fill element. The bar is driven imperatively:
// anything that wants to report load progress looks this element up by id and
// sets its width. No React state, props or context involved, so the App tree
// never re-renders mid-load and clobbers it.
export const LOADING_BAR_ID = 'loading-splash-bar'

declare global {
  interface Window {
    setLoadingProgress?: (fraction: number) => void
  }
}

// Overall load is split into weighted stages so the bar advances monotonically
// from 0 to 1 across downloads -> material prep -> shader compile.
const STAGE_WEIGHTS = { download: 0.6, prepare: 0.3, compile: 0.1 } as const
export type LoadingStage = keyof typeof STAGE_WEIGHTS
const STAGE_ORDER: LoadingStage[] = ['download', 'prepare', 'compile']

const clamp01 = (n: number) => Math.max(0, Math.min(1, n))
let current = 0

// Set the bar fill width (clamped, monotonic so it never jumps backwards between
// overlapping stages). Exposed on `window` so every code path that contributes to
// load time — React components, hooks, or raw three.js loader callbacks — can
// report progress dependency-lessly. No-ops once the bar has unmounted.
export function setLoadingProgress(fraction: number): void {
  current = Math.max(current, clamp01(fraction))
  const el = document.getElementById(LOADING_BAR_ID)
  if (el) el.style.width = `${current * 100}%`
}

// Report progress within a named stage (0..1); maps it onto the stage's slice of
// the overall bar.
export function reportLoadingStage(stage: LoadingStage, fraction: number): void {
  let base = 0
  for (const s of STAGE_ORDER) {
    if (s === stage) break
    base += STAGE_WEIGHTS[s]
  }
  setLoadingProgress(base + STAGE_WEIGHTS[stage] * clamp01(fraction))
}

if (typeof window !== 'undefined') {
  window.setLoadingProgress = setLoadingProgress
}

// Stage 1: downloads. three routes useGLTF / useLoader through the
// DefaultLoadingManager, so this single hook covers room.gltf, its textures, the
// HDR environment and the projector's leaves — no per-loader wiring.
DefaultLoadingManager.onProgress = (_url, loaded, total) => {
  reportLoadingStage('download', total > 0 ? loaded / total : 0)
}
